// THE ORGANISATION RASTER, VERSION 1 — one standardised set of comparable cells (BUILD-BRIEF-R3
// §1). It lives in `src/shared/` because BOTH sides read it: the server parses and scores
// against it, the console renders its labels. `src/server/profile/raster.v1.ts` re-exports it
// so the server-side path named in the brief keeps working.
//
// A cell is an enum, a band, a number with a unit, a boolean, a year, a month or a reference.
// COMPARABLE OR IT IS NOT A CELL: free prose belongs in the dossier, and a `text` cell carries
// `comparable: false` so it never enters coverage or an axis. A cell nobody filled reads "not
// collected"; a cell an agent estimated says so (`method 'estimated'` with a confidence).
//
// Facts are keyed `profile.<block>.<cell>` and their VALUE IS ALWAYS TEXT — `parseCell` is the
// one place that turns text into a typed value, and the one place that refuses.
//
// ═══════════════════════════════════════════════════════════════════════════════
// BAND CALIBRATION — why the cut points are where they are (R3-RESEARCH §2)
// ═══════════════════════════════════════════════════════════════════════════════
//
// The round-3 research pass could not read a Vaud annual report (its PDF extraction failed) and
// left `fte_band` and `budget_band` PROVISIONAL. Three real Canton de Vaud rapports annuels were
// downloaded and read as PDFs on 2026-09-02 before this file was locked:
//
//   1. SMALL — Fondation Mère Sofia, Lausanne (VD). Rapport d'activité 2024.
//      39 collaborateurs·trices at 31.12.2024 (headcount, not FTE).
//      Total des produits CHF 3'216'444 · total des charges CHF 3'232'176 · résultat −15'732.
//      https://meresofia.astera.ch/storage/app/media/uploaded-files/Documentation/Rapport%20activit%C3%A9/R.A.-2024.pdf
//      → fte_band `10_49`, budget_band `1_5m`.
//
//   2. MID — Fondation Le Relais, Morges (VD). Journal du rapport annuel 2024.
//      "180 employé·e·s et chargé·e·s de cours" · "budget annuel s'élève à 18 millions CHF en
//      2024" · 14 structures · 23 financeurs.
//      https://www.relais.ch/wp-content/uploads/FONDATION_LE_RELAIS_RAPPORT_ANNUEL_2024.pdf
//      → fte_band `50_199`, budget_band `10_30m`.
//
//   3. LARGE — Fondation Eben-Hézer, Lausanne (VD). Rapport d'activité 2025.
//      1150 collaboratrices et collaborateurs; 845,13 EPT (297,46 Eben-Hézer Lausanne + 284,62
//      Cité du Genévrier + 263,05 Pôle Grand Âge). Produits d'exploitation CHF 125,3 mio ·
//      charges de fonctionnement 124,7 mio · total du bilan 108,8 mio · fonds propres 39,1 %.
//      https://www.eben-hezer.ch/files/eben-hezer/la_fondation/documents/f-e-h-RA-2025-A4-WEB.pdf
//      → fte_band `500_999`, budget_band `80m_plus`.
//
//   4. AGGREGATE — AVOP, 61 member institutions, ~8'000 staff (R3-RESEARCH §1) → a mean of ~131
//      staff per member, i.e. the modal Vaud social-sector institution sits in `50_199`.
//
// WHAT THE DATA CHANGED. The four provisional cut points (10 · 50 · 200 · 500 FTE; 1 · 5 · 10 ·
// 30 m CHF) separate the three organisations and the AVOP mean cleanly and are KEPT EXACTLY.
// What the data refused was the open-ended TOP band: `500_plus` and `30m_plus` would have put
// Eben-Hézer (845 EPT, CHF 125 mio) in the same cell as a 520-EPT, CHF 32 mio fondation, and the
// top of this market is precisely where the operator's accounts are. So each top band is split
// once, at 1'000 FTE and at CHF 80 mio — the two edges that fall between the AVOP mean and
// Eben-Hézer with room on both sides. This also makes BUILD-BRIEF-R3 §5 consistent with itself:
// its fit rules already name `500_999` and `30_80m`, values the provisional list did not carry.
//
// No other band moved, and no figure above was inferred: each is quoted from the page cited.
// ═══════════════════════════════════════════════════════════════════════════════

import { REGIME_VALUES, NUMERIC_QUALIFIERS, QUALIFIER_SIGN, splitQualifier, type NumericQualifier } from './network.v1.js';

// Re-exported so a caller that reaches for the qualifier primitives here (as BUILD-BRIEF-R5
// describes them) still finds them — they are DECLARED in `network.v1.ts` (zero imports, so
// `raster.v1.ts` may import `REGIME_VALUES` from it without a cycle; see that file's header).
export { NUMERIC_QUALIFIERS, QUALIFIER_SIGN, splitQualifier } from './network.v1.js';
export type { NumericQualifier } from './network.v1.js';

// ── the shapes ──────────────────────────────────────────────────────────────

export type RasterCellType =
  | 'enum' | 'enum_multi' | 'band' | 'int' | 'number' | 'bool'
  | 'year' | 'month' | 'person_ref' | 'organisation_ref' | 'text';

export interface RasterValue {
  value: string;
  /** English — the i18n key. `labelFor('de', …)` looks up the German. */
  label: string;
}

/**
 * A band's edges. `min` is INCLUSIVE, `max` is EXCLUSIVE, `null` is unbounded — so `10_49` is
 * [10, 50) and the integer 49 is its last member. `bandFor()` is the one reader of these.
 */
export interface RasterBand extends RasterValue {
  min: number | null;
  max: number | null;
}

export interface RasterCell {
  key: string;
  label: string;
  type: RasterCellType;
  unit?: string;
  values?: RasterValue[];
  bands?: RasterBand[];
  /** Where a value for this cell is normally read. Names a source, never a guess. */
  sources_hint: string;
  /** Whether the `fit` axis of BUILD-BRIEF-R3 §5 reads this cell. */
  fit_relevant: boolean;
  /** False for free text: it is never used for analysis and never counted in coverage. */
  comparable: boolean;
  /** The organisations this cell is asked of; absent = every organisation (R3g §7). */
  applies_when?: AppliesWhen;
}

export interface RasterBlock {
  key: string;
  label: string;
  cells: RasterCell[];
  /** The triggers block: computed nightly from signals and events, never proposed, never counted. */
  computed?: boolean;
}

export const RASTER_VERSION = 'v1';

/**
 * APPLICABILITY (BUILD-BRIEF-R3g §7, decided 2026-09-03). A cell may declare the legal forms
 * (and, later, sub-sectors) it is asked of. A cell that does not apply to an organisation is not
 * asked (zero tokens), reads "not applicable" — a fourth state beside filled / not collected /
 * pending — and leaves the coverage figure, so a cabinet or a Sàrl is not measured as a failed
 * foundation. Absent → the cell applies to every organisation. When in doubt, it applies.
 */
export interface AppliesWhen {
  legal_form?: readonly string[];
  subsector?: readonly string[];
}
/** The forms the institution-only cells are asked of: beds, places, clients, accreditation,
 *  subsidy dependence, ZEWO, and the board (a company's conseil d'administration is not what this
 *  record is for). Companies, cooperatives, sole practitioners and `other` are not asked. */
export const INSTITUTIONS: readonly string[] = ['association', 'fondation', 'corporation_droit_public', 'etablissement_droit_public'];
const INSTITUTIONS_ONLY: AppliesWhen = { legal_form: INSTITUTIONS };

// ── the declaration DSL ─────────────────────────────────────────────────────
//
// Values are declared as [value, en, de, fr] so a translation can never drift away from the
// value it belongs to. The DE/FR registries below are filled as the raster is built.

type ValueSpec = readonly [value: string, en: string, de: string, fr: string];
type BandSpec = readonly [value: string, en: string, de: string, fr: string, min: number | null, max: number | null];

interface CellSpec {
  key: string;
  en: string;
  de: string;
  fr: string;
  type: RasterCellType;
  unit?: string;
  values?: readonly ValueSpec[];
  bands?: readonly BandSpec[];
  sources_hint: string;
  fit_relevant?: boolean;
  /** Defaults to `type !== 'text'`. */
  comparable?: boolean;
  /** The organisations this cell is asked of; absent = all (R3g §7). */
  applies_when?: AppliesWhen;
}

interface BlockSpec {
  key: string;
  en: string;
  de: string;
  fr: string;
  computed?: boolean;
  cells: readonly CellSpec[];
}

const DE_BLOCKS: Record<string, string> = {};
const FR_BLOCKS: Record<string, string> = {};
const DE_CELLS: Record<string, string> = {};
const FR_CELLS: Record<string, string> = {};
const DE_VALUES: Record<string, string> = {};
const FR_VALUES: Record<string, string> = {};

function buildBlock(spec: BlockSpec): RasterBlock {
  DE_BLOCKS[spec.key] = spec.de;
  FR_BLOCKS[spec.key] = spec.fr;
  const cells = spec.cells.map((c): RasterCell => {
    const path = `${spec.key}.${c.key}`;
    DE_CELLS[path] = c.de;
    FR_CELLS[path] = c.fr;
    const values = c.values?.map(([value, en, de, fr]) => {
      DE_VALUES[`${path}.${value}`] = de;
      FR_VALUES[`${path}.${value}`] = fr;
      return { value, label: en };
    });
    const bands = c.bands?.map(([value, en, de, fr, min, max]) => {
      DE_VALUES[`${path}.${value}`] = de;
      FR_VALUES[`${path}.${value}`] = fr;
      return { value, label: en, min, max };
    });
    return {
      key: c.key,
      label: c.en,
      type: c.type,
      ...(c.unit ? { unit: c.unit } : {}),
      ...(values ? { values } : {}),
      ...(bands ? { bands } : {}),
      sources_hint: c.sources_hint,
      fit_relevant: c.fit_relevant ?? false,
      comparable: c.comparable ?? c.type !== 'text',
      ...(c.applies_when ? { applies_when: c.applies_when } : {}),
    };
  });
  return { key: spec.key, label: spec.en, cells, ...(spec.computed ? { computed: true as const } : {}) };
}

// ── the vocabularies that are shared or long ────────────────────────────────

/** The 26 cantons, by their two-letter code. */
const CANTONS: readonly ValueSpec[] = [
  ['AG', 'Aargau', 'Aargau', 'Argovie'],
  ['AI', 'Appenzell Innerrhoden', 'Appenzell Innerrhoden', 'Appenzell Rhodes-Intérieures'],
  ['AR', 'Appenzell Ausserrhoden', 'Appenzell Ausserrhoden', 'Appenzell Rhodes-Extérieures'],
  ['BE', 'Bern', 'Bern', 'Berne'],
  ['BL', 'Basel-Landschaft', 'Basel-Landschaft', 'Bâle-Campagne'],
  ['BS', 'Basel-Stadt', 'Basel-Stadt', 'Bâle-Ville'],
  ['FR', 'Fribourg', 'Freiburg', 'Fribourg'],
  ['GE', 'Geneva', 'Genf', 'Genève'],
  ['GL', 'Glarus', 'Glarus', 'Glaris'],
  ['GR', 'Grisons', 'Graubünden', 'Grisons'],
  ['JU', 'Jura', 'Jura', 'Jura'],
  ['LU', 'Lucerne', 'Luzern', 'Lucerne'],
  ['NE', 'Neuchâtel', 'Neuenburg', 'Neuchâtel'],
  ['NW', 'Nidwalden', 'Nidwalden', 'Nidwald'],
  ['OW', 'Obwalden', 'Obwalden', 'Obwald'],
  ['SG', 'St. Gallen', 'St. Gallen', 'Saint-Gall'],
  ['SH', 'Schaffhausen', 'Schaffhausen', 'Schaffhouse'],
  ['SO', 'Solothurn', 'Solothurn', 'Soleure'],
  ['SZ', 'Schwyz', 'Schwyz', 'Schwytz'],
  ['TG', 'Thurgau', 'Thurgau', 'Thurgovie'],
  ['TI', 'Ticino', 'Tessin', 'Tessin'],
  ['UR', 'Uri', 'Uri', 'Uri'],
  ['VD', 'Vaud', 'Waadt', 'Vaud'],
  ['VS', 'Valais', 'Wallis', 'Valais'],
  ['ZG', 'Zug', 'Zug', 'Zoug'],
  ['ZH', 'Zurich', 'Zürich', 'Zurich'],
] as const;

// ── the six blocks ──────────────────────────────────────────────────────────

const IDENTITY = buildBlock({
  key: 'identity',
  en: 'Identity and structure',
  de: 'Identität und Struktur',
  fr: 'Identité et structure',
  cells: [
    {
      key: 'legal_form', en: 'Legal form', de: 'Rechtsform', fr: 'Forme juridique',
      type: 'enum',
      sources_hint: 'Zefix REST (round 2) or LINDAS; the two public-law forms are read from the cantonal decree, not from Zefix.',
      values: [
        ['association', 'Association', 'Verein', 'Association'],
        ['fondation', 'Foundation', 'Stiftung', 'Fondation'],
        ['sa', 'Public limited company (SA)', 'Aktiengesellschaft (AG)', 'Société anonyme (SA)'],
        ['sarl', 'Limited liability company (Sàrl)', 'GmbH', 'Société à responsabilité limitée (Sàrl)'],
        ['cooperative', 'Cooperative', 'Genossenschaft', 'Société coopérative'],
        ['corporation_droit_public', 'Public-law corporation', 'Körperschaft des öffentlichen Rechts', 'Corporation de droit public'],
        ['etablissement_droit_public', 'Public-law establishment', 'Anstalt des öffentlichen Rechts', 'Établissement de droit public'],
        ['raison_individuelle', 'Sole proprietorship', 'Einzelunternehmen', 'Raison individuelle'],
        ['other', 'Other', 'Andere', 'Autre'],
      ],
    },
    {
      key: 'uid', en: 'UID', de: 'UID', fr: 'IDE',
      type: 'text',
      sources_hint: 'uid.admin.ch or LINDAS; the canonical form is CHE-123.456.789.',
    },
    {
      key: 'canton', en: 'Canton', de: 'Kanton', fr: 'Canton',
      type: 'enum', values: CANTONS,
      sources_hint: 'The seat in the commercial register, or the imprint of the organisation’s own site.',
    },
    {
      key: 'seat', en: 'Seat', de: 'Sitz', fr: 'Siège',
      type: 'text',
      sources_hint: 'The commune of the registered seat, spelled as the register spells it.',
    },
    {
      key: 'founded_year', en: 'Founded', de: 'Gegründet', fr: 'Fondée en',
      type: 'year',
      sources_hint: 'The year the CURRENT legal entity was founded — the register’s incorporation when the site and the register disagree (a foundation that succeeded a 1933 association is founded in 1976). The older institutional origin ("depuis 1933") goes into the dossier, not into this cell.',
    },
    {
      key: 'parent', en: 'Parent organisation', de: 'Trägerorganisation', fr: 'Organisation faîtière',
      type: 'organisation_ref',
      sources_hint: 'Named on the site or in the annual report; resolved against organisations on the record. An organisation that describes itself as independent (its own committee, no parent body) takes `none` — a counted absence, not "not collected".',
    },
    {
      key: 'umbrella_memberships', en: 'Umbrella memberships', de: 'Verbandsmitgliedschaften', fr: 'Affiliations faîtières',
      type: 'enum_multi',
      sources_hint: 'Federation member directories (§6): the federation\'s own member page (insertion-vaud.ch/membre/<slug>, avop.ch, artiset.ch …) names the member; the organisation\'s "partenaires" page is a lead, not membership. ARTISET has been the umbrella since 2022 and its branches may all be true at once. A federation not in this list is `other`, named in the dossier.',
      values: [
        ['artiset', 'ARTISET', 'ARTISET', 'ARTISET'],
        ['insos', 'INSOS', 'INSOS', 'INSOS'],
        ['curaviva', 'CURAVIVA', 'CURAVIVA', 'CURAVIVA'],
        ['youvita', 'YOUVITA', 'YOUVITA', 'YOUVITA'],
        ['avop', 'AVOP', 'AVOP', 'AVOP'],
        ['heviva', 'HévivA', 'HévivA', 'HévivA'],
        ['federems', 'FEDEREMS', 'FEDEREMS', 'FEDEREMS'],
        ['grea', 'GREA', 'GREA', 'GREA'],
        ['fachverband_sucht', 'Fachverband Sucht', 'Fachverband Sucht', 'Fachverband Sucht'],
        ['zewo', 'ZEWO', 'ZEWO', 'ZEWO'],
        // The insertion sector's two federations (2026-09-05, the SemoNord benchmark: a run filed
        // `other` for a membership the federation's own page states), and the CIFÉA community
        // (BUILD-BRIEF-R5 §1.5: CORREF is a member AND CIFÉA is its payer — the membership is this
        // cell, the money is a `payer` link; both are filed).
        ['insertion_vaud', 'Insertion Vaud', 'Insertion Vaud', 'Insertion Vaud'],
        ['insertion_suisse', 'Insertion Suisse', 'Arbeitsintegration Schweiz', 'Insertion Suisse'],
        ['cifea', 'CIFÉA', 'CIFÉA', 'CIFÉA'],
        ['other', 'Other', 'Andere', 'Autre'],
      ],
    },
    {
      key: 'noga_code', en: 'NOGA code', de: 'NOGA-Code', fr: 'Code NOGA',
      type: 'text',
      sources_hint: 'The federal business register’s activity code, when the organisation publishes it.',
    },
    {
      key: 'subsector', en: 'Sub-sector', de: 'Teilbereich', fr: 'Sous-secteur',
      type: 'enum_multi', fit_relevant: true,
      sources_hint: 'The first eight are the DGCS Vaud headings of the list of établissements socio-éducatifs; the rest describe the wider social sector. Read it off the organisation’s OWN mission and catalogue, never off the directory that listed it (a social-directory entry put an orientation centre under `addiction`): orientation, reinsertion and adult basic education are `integration,education`.',
      values: [
        ['deficience_intellectuelle', 'Intellectual disability', 'Geistige Behinderung', 'Déficience intellectuelle'],
        ['handicap_physique', 'Physical disability', 'Körperbehinderung', 'Handicap physique'],
        ['polyhandicap', 'Multiple disability', 'Mehrfachbehinderung', 'Polyhandicap'],
        ['handicap_sensoriel', 'Sensory disability', 'Sinnesbehinderung', 'Handicap sensoriel'],
        ['handicap_psychique', 'Psychiatric disability', 'Psychische Behinderung', 'Handicap psychique'],
        ['difficultes_sociales_addictions', 'Severe social difficulty or addiction', 'Schwere soziale Notlage oder Sucht', 'Grandes difficultés sociales ou addictions'],
        ['ateliers_productifs', 'Productive workshops', 'Produktionswerkstätten', 'Ateliers à vocation productive'],
        ['ateliers_socialisants', 'Socialising workshops', 'Sozialisierende Werkstätten', 'Ateliers à vocation socialisante'],
        ['addiction', 'Addiction', 'Sucht', 'Addiction'],
        ['child_youth', 'Children and youth', 'Kinder und Jugendliche', 'Enfance et jeunesse'],
        ['elderly_care', 'Elderly care', 'Alterspflege', 'Prise en charge des aînés'],
        ['integration', 'Integration', 'Integration', 'Intégration'],
        ['social_assistance', 'Social assistance', 'Sozialhilfe', 'Aide sociale'],
        ['health', 'Health', 'Gesundheit', 'Santé'],
        ['public_admin', 'Public administration', 'Öffentliche Verwaltung', 'Administration publique'],
        ['education', 'Education', 'Bildung', 'Formation'],
        ['other', 'Other', 'Andere', 'Autre'],
      ],
    },
  ],
});

const MANDATE = buildBlock({
  key: 'mandate',
  en: 'Mandate and offering',
  de: 'Auftrag und Angebot',
  fr: 'Mandat et prestations',
  cells: [
    {
      key: 'service_lines', en: 'Service lines', de: 'Leistungsbereiche', fr: 'Lignes de prestations',
      type: 'enum_multi',
      sources_hint: 'The organisation’s own "nos prestations" pages and the annual report.',
      values: [
        ['residential', 'Residential', 'Wohnen', 'Hébergement'],
        ['day_structure', 'Day structure', 'Tagesstruktur', 'Structure de jour'],
        ['sheltered_work', 'Sheltered work', 'Geschützte Arbeit', 'Travail protégé'],
        ['ambulatory', 'Ambulatory', 'Ambulant', 'Ambulatoire'],
        ['counselling', 'Counselling', 'Beratung', 'Conseil'],
        ['prevention', 'Prevention', 'Prävention', 'Prévention'],
        ['training', 'Training', 'Ausbildung', 'Formation'],
        ['administration', 'Administration', 'Administration', 'Administration'],
        ['other', 'Other', 'Andere', 'Autre'],
      ],
    },
    {
      key: 'beneficiary_groups', en: 'Beneficiary groups', de: 'Zielgruppen', fr: 'Publics bénéficiaires',
      type: 'enum_multi',
      sources_hint: 'The mission statement, the umbrella\'s member page ("publics"), the annual report; never inferred from a photograph. WHO is served, never under which scheme (that is `regimes`). youth = minors and transition measures to 25 ("jeunes", "15-25 ans"); young_adults_18_25 = an adult public the source bounds 18–25 ("18-25 ans", "jeunes adultes"). seniors_50plus = jobseekers over fifty ("seniors"); elderly = EMS clientele ("personnes âgées"). health_limitations = "limitations de santé", "atteintes dans leur santé"; adults_disability = "handicap". single_parent_families beside families when the source names both.',
      values: [
        ['adults_disability', 'Adults with a disability', 'Erwachsene mit Behinderung', 'Adultes en situation de handicap'],
        ['children_disability', 'Children with a disability', 'Kinder mit Behinderung', 'Enfants en situation de handicap'],
        ['addiction', 'People with an addiction', 'Suchtbetroffene', 'Personnes dépendantes'],
        ['youth', 'Young people', 'Jugendliche', 'Jeunes'],
        ['young_adults_18_25', 'Young adults 18–25', 'Junge Erwachsene 18–25', 'Jeunes adultes 18–25 ans'],
        // Adults as such — an orientation or adult-education centre's public (CORREF, 2026-09-05):
        // neither "general public" nor a disability.
        ['adults', 'Adults', 'Erwachsene', 'Adultes'],
        ['families', 'Families', 'Familien', 'Familles'],
        ['single_parent_families', 'Single-parent families', 'Einelternfamilien', 'Familles monoparentales'],
        ['highly_qualified', 'Highly qualified people', 'Hochqualifizierte', 'Personnes hautement qualifiées'],
        ['health_limitations', 'People with health limitations', 'Menschen mit gesundheitlichen Einschränkungen', 'Personnes avec des limitations de santé'],
        ['elderly', 'Elderly people', 'Ältere Menschen', 'Personnes âgées'],
        ['migrants', 'Migrants', 'Migrantinnen und Migranten', 'Personnes migrantes'],
        ['unemployed', 'Unemployed people', 'Erwerbslose', 'Personnes sans emploi'],
        ['without_qualifications', 'People without qualifications', 'Personen ohne Abschluss', 'Personnes sans qualification'],
        ['seniors_50plus', 'Seniors (50+ jobseekers)', 'Ältere Stellensuchende 50+', 'Seniors 50+'],
        ['general_public', 'General public', 'Allgemeine Bevölkerung', 'Grand public'],
      ],
    },
    {
      key: 'regimes', en: 'Regimes served', de: 'Leistungssysteme', fr: 'Régimes des bénéficiaires',
      type: 'enum_multi', fit_relevant: true,
      sources_hint: 'The "publics", "bénéficiaires" or "mandants" line of the mission page, the umbrella\'s member page, the annual report. A regime is the scheme that sends or pays for the people served — never inferred from a payer\'s name alone. UNDER WHICH SCHEME, never who (that is `beneficiary_groups`).',
      values: REGIME_VALUES,
    },
    {
      key: 'capacity_places', en: 'Places', de: 'Plätze', fr: 'Places',
      applies_when: INSTITUTIONS_ONLY,
      type: 'int', unit: 'places',
      sources_hint: 'The annual report, the cantonal authorisation, or the organisation\'s own page per measure ("finance 20 places par année"). Several measures each stating a figure add up to ONE value for this cell; when a measure states none, file the sum as a floor ("≥116") and put the decomposition in the dossier. The DGCS list carries no capacity.',
    },
    {
      key: 'capacity_beds', en: 'Beds', de: 'Betten', fr: 'Lits',
      applies_when: INSTITUTIONS_ONLY,
      type: 'int', unit: 'beds',
      sources_hint: 'The annual report, or the opendata.swiss Pflegeheime dataset for an EMS.',
    },
    {
      key: 'clients_per_year', en: 'Clients per year', de: 'Klientinnen und Klienten pro Jahr', fr: 'Bénéficiaires par an',
      applies_when: INSTITUTIONS_ONLY,
      type: 'int', unit: 'persons',
      sources_hint: 'The annual report’s own figure, or the most recent published one ("environ 250 jeunes par année" → "~250", as_of the publication’s date); never the sum of two overlapping counts. The newest dated source wins over an undated homepage. A multi-year series (1’271 → 1’702 over 2020–2024) puts the LATEST year in the cell with its as_of and the series in the dossier; each later year supersedes, and the superseded rows are the history.',
    },
    {
      key: 'coverage_geo', en: 'Geographic coverage', de: 'Einzugsgebiet', fr: 'Couverture géographique',
      type: 'enum',
      sources_hint: 'The sites list and the mandate; canton is the default reading for a cantonal mandate.',
      values: [
        ['commune', 'One commune', 'Eine Gemeinde', 'Une commune'],
        ['district', 'A district', 'Ein Bezirk', 'Un district'],
        ['canton', 'One canton', 'Ein Kanton', 'Un canton'],
        ['multi_canton', 'Several cantons', 'Mehrere Kantone', 'Plusieurs cantons'],
        ['national', 'National', 'Landesweit', 'National'],
      ],
    },
    {
      key: 'accreditation', en: 'Accreditation', de: 'Anerkennung', fr: 'Reconnaissance',
      applies_when: INSTITUTIONS_ONLY,
      type: 'enum',
      sources_hint: 'The legal basis to operate and bill — not where the money comes from, which is funding_model. The DGCS list implies cantonal_mandate.',
      values: [
        ['cantonal_mandate', 'Cantonal mandate', 'Kantonaler Leistungsauftrag', 'Mandat cantonal'],
        ['lamal', 'LAMal', 'KVG', 'LAMal'],
        ['both', 'Both', 'Beides', 'Les deux'],
        ['none', 'Neither', 'Keine', 'Aucune'],
      ],
    },
  ],
});

const SIZE_MONEY = buildBlock({
  key: 'size_money',
  en: 'Size and money',
  de: 'Grösse und Finanzen',
  fr: 'Taille et finances',
  cells: [
    {
      key: 'fte_band', en: 'FTE band', de: 'VZÄ-Band', fr: 'Tranche EPT',
      type: 'band', fit_relevant: true,
      sources_hint: 'Derived from `fte` with bandFor(), or read from the annual report’s staff page. Calibrated on three Vaud annual reports — see the header.',
      bands: [
        ['lt10', 'Under 10', 'Unter 10', 'Moins de 10', 0, 10],
        ['10_49', '10 to 49', '10 bis 49', '10 à 49', 10, 50],
        ['50_199', '50 to 199', '50 bis 199', '50 à 199', 50, 200],
        ['200_499', '200 to 499', '200 bis 499', '200 à 499', 200, 500],
        ['500_999', '500 to 999', '500 bis 999', '500 à 999', 500, 1000],
        ['1000_plus', '1000 and above', '1000 und mehr', '1000 et plus', 1000, null],
      ],
    },
    {
      key: 'fte', en: 'FTE', de: 'VZÄ', fr: 'EPT',
      type: 'number', unit: 'FTE',
      sources_hint: 'The annual report’s own "postes de travail (EPT)" figure. A headcount is NOT an FTE; if only a headcount is published, read it as such and leave this cell not collected.',
    },
    {
      key: 'budget_band', en: 'Budget band', de: 'Budgetband', fr: 'Tranche budgétaire',
      type: 'band', fit_relevant: true,
      sources_hint: 'Derived from `revenue_chf` with bandFor(), or the annual report’s stated annual budget. Calibrated on three Vaud annual reports — see the header.',
      bands: [
        ['lt1m', 'Under CHF 1m', 'Unter CHF 1 Mio.', 'Moins de CHF 1 mio', 0, 1_000_000],
        ['1_5m', 'CHF 1m to 5m', 'CHF 1 bis 5 Mio.', 'CHF 1 à 5 mio', 1_000_000, 5_000_000],
        ['5_10m', 'CHF 5m to 10m', 'CHF 5 bis 10 Mio.', 'CHF 5 à 10 mio', 5_000_000, 10_000_000],
        ['10_30m', 'CHF 10m to 30m', 'CHF 10 bis 30 Mio.', 'CHF 10 à 30 mio', 10_000_000, 30_000_000],
        ['30_80m', 'CHF 30m to 80m', 'CHF 30 bis 80 Mio.', 'CHF 30 à 80 mio', 30_000_000, 80_000_000],
        ['80m_plus', 'CHF 80m and above', 'CHF 80 Mio. und mehr', 'CHF 80 mio et plus', 80_000_000, null],
      ],
    },
    {
      key: 'revenue_chf', en: 'Revenue', de: 'Ertrag', fr: 'Produits',
      type: 'number', unit: 'CHF',
      sources_hint: 'Total operating income ("total des produits" / "produits d’exploitation") from the annual report. The only per-organisation financial source (§6); ZEWO publishes none.',
    },
    {
      key: 'result_chf', en: 'Result', de: 'Ergebnis', fr: 'Résultat',
      type: 'number', unit: 'CHF',
      sources_hint: 'The annual result as the report states it, sign included. A loss is a negative number, never an absolute value.',
    },
    {
      key: 'equity_ratio_pct', en: 'Equity ratio', de: 'Eigenkapitalquote', fr: 'Taux de fonds propres',
      type: 'number', unit: '%',
      sources_hint: 'The report’s own "taux de fonds propres", or equity divided by the balance-sheet total when both are printed.',
    },
    {
      key: 'subsidy_dependence_pct', en: 'Subsidy dependence', de: 'Subventionsabhängigkeit', fr: 'Dépendance aux subventions',
      applies_when: INSTITUTIONS_ONLY,
      type: 'number', unit: '%',
      sources_hint: 'Public subsidies as a share of operating income, from the report’s "origine des produits" table.',
    },
    {
      key: 'funding_model', en: 'Funding model', de: 'Finanzierungsmodell', fr: 'Modèle de financement',
      type: 'enum_multi',
      sources_hint: 'The income table of the annual report, or the mandants and the sales the site names. A list: a mixed model is every source that applies (cantonal mandates + fees + donations …). Per-diem billing is distinct from a lump-sum performance contract; AI/OFAS money is distinct from other federal money; a cantonal service (DGCS, DIRIS, DGEJ, SPAS …) is `cantonal`, a commune is `communal`. A named amount ("CHF 80’000 par an") is not a value of this cell: it is a `funder` link (propose_link, amount_chf, the year) and dossier material.',
      values: [
        ['contrat_prestations', 'Performance contract', 'Leistungsvertrag', 'Contrat de prestations'],
        ['forfait_journalier', 'Per-diem rate', 'Tagespauschale', 'Forfait journalier'],
        ['lamal', 'LAMal', 'KVG', 'LAMal'],
        ['ai_ofas', 'AI / OFAS contributions', 'IV-/BSV-Beiträge', 'Contributions AI / OFAS'],
        ['federal_other', 'Other federal funding', 'Andere Bundesmittel', 'Autres fonds fédéraux'],
        // The canton was missing between the Confederation and the commune (2026-09-05).
        ['cantonal', 'Cantonal subsidies and mandates', 'Kantonale Subventionen und Mandate', 'Subventions et mandats cantonaux'],
        ['communal', 'Communal subsidies', 'Gemeindesubventionen', 'Subventions communales'],
        ['donations', 'Donations and legacies', 'Spenden und Legate', 'Dons et legs'],
        // An association's cotisations — the register's own "ressources" clause names them first.
        // No `membership_fees` beside it and no `public_subsidy` (BUILD-BRIEF-R5 §1.5): one token
        // per concept, and an unstated level is not a comparable value.
        ['member_fees', 'Membership fees', 'Mitgliederbeiträge', 'Cotisations des membres'],
        ['fees', 'Fees and sales', 'Gebühren und Verkäufe', 'Taxes et ventes'],
        ['other', 'Other', 'Andere', 'Autre'],
      ],
    },
    {
      key: 'fiscal_year_end', en: 'Fiscal year end', de: 'Ende des Geschäftsjahres', fr: 'Clôture de l’exercice',
      type: 'month',
      sources_hint: 'The month the annual accounts close on, as a number 1–12.',
    },
    {
      key: 'zewo_certified', en: 'ZEWO certified', de: 'ZEWO-zertifiziert', fr: 'Certifiée ZEWO',
      applies_when: INSTITUTIONS_ONLY,
      type: 'bool',
      sources_hint: 'The ZEWO detail page (§6) — true, read. An organisation the ZEWO directory does not list is `false` with method `estimated` (the evidence is an absence: cite the directory search snapshot, confidence at most 0.7). ZEWO carries the certification, never a financial figure.',
    },
  ],
});

const OPERATING = buildBlock({
  key: 'operating',
  en: 'Operating model and maturity',
  de: 'Betriebsmodell und Reifegrad',
  fr: 'Modèle opérationnel et maturité',
  cells: [
    {
      key: 'sites_count', en: 'Sites', de: 'Standorte', fr: 'Sites',
      type: 'int', fit_relevant: true,
      sources_hint: 'The organisation’s own sites or contact page; count addresses, not services.',
    },
    {
      key: 'quality_labels', en: 'Quality labels', de: 'Qualitätslabels', fr: 'Labels qualité',
      type: 'enum_multi', fit_relevant: true,
      sources_hint: 'The certificate logos or the quality page ("charte et qualité"). `eduqua` is proposable only when service_lines includes `training`; `in_qualis` is the insertion sector\'s label (the former AOMAS norm — a page saying AOMAS means in_qualis). The certification year goes into the dossier. A cantonal "Label Qualité" was not confirmed and is not in this list.',
      values: [
        ['iso9001', 'ISO 9001', 'ISO 9001', 'ISO 9001'],
        ['quatheda', 'QuaTheDA', 'QuaTheDA', 'QuaTheDA'],
        ['ofas2000', 'OFAS/BSV 2000', 'BSV 2000', 'OFAS 2000'],
        ['eduqua', 'eduQua', 'eduQua', 'eduQua'],
        ['in_qualis', 'IN-Qualis (ex-AOMAS)', 'IN-Qualis (ehem. AOMAS)', 'IN-Qualis (ex-AOMAS)'],
        ['other', 'Other', 'Andere', 'Autre'],
        ['none', 'None', 'Keine', 'Aucun'],
      ],
    },
    {
      key: 'strategy_published', en: 'Strategy published', de: 'Strategie veröffentlicht', fr: 'Stratégie publiée',
      type: 'bool',
      sources_hint: 'True only when the strategy document or its summary is actually on the site.',
    },
    {
      key: 'strategy_period_end', en: 'Strategy period ends', de: 'Strategieperiode endet', fr: 'Fin de la période stratégique',
      type: 'year',
      sources_hint: 'The last year of the published strategy period ("plan stratégique 2025–2029" → 2029).',
    },
    {
      key: 'client_dossier_system', en: 'Client dossier system', de: 'Klientendossier-System', fr: 'Système de dossier client',
      type: 'text',
      sources_hint: 'Named in a job advert, a tender or the annual report. Free text, never compared.',
    },
    {
      key: 'erp', en: 'ERP', de: 'ERP', fr: 'ERP',
      type: 'text',
      sources_hint: 'Named in a job advert or a tender. Free text, never compared.',
    },
    {
      key: 'online_services', en: 'Online services', de: 'Online-Dienste', fr: 'Services en ligne',
      type: 'bool',
      sources_hint: 'A portal, an online form or an e-service the organisation offers its beneficiaries.',
    },
    {
      key: 'dpo_named', en: 'Data protection officer named', de: 'Datenschutzberater benannt', fr: 'Conseiller à la protection des données nommé',
      type: 'bool',
      sources_hint: 'The privacy notice or the imprint names a PERSON for data protection → true. A notice that gives only a function or a mailbox (dpo@…) names nobody → false, read: the answer is counted, not missing. not_found is for an organisation with no privacy notice to read at all.',
    },
    {
      key: 'annual_report_online', en: 'Annual report online', de: 'Jahresbericht online', fr: 'Rapport annuel en ligne',
      type: 'bool',
      sources_hint: 'True when the `annual_report` monitor found a downloadable report on the site.',
    },
    {
      key: 'digital_maturity', en: 'Digital maturity', de: 'Digitale Reife', fr: 'Maturité numérique',
      type: 'band',
      sources_hint: 'ESTIMATED, never read: an agent proposing this cell must carry method `estimated`, a supporting quote and a confidence at or below 0.7.',
      bands: [
        ['1_initial', '1 · Initial', '1 · Initial', '1 · Initial', 1, 2],
        ['2_managed', '2 · Managed', '2 · Gesteuert', '2 · Géré', 2, 3],
        ['3_defined', '3 · Defined', '3 · Definiert', '3 · Défini', 3, 4],
        ['4_measured', '4 · Measured', '4 · Gemessen', '4 · Mesuré', 4, 5],
        ['5_optimising', '5 · Optimising', '5 · Optimierend', '5 · En optimisation', 5, null],
      ],
    },
  ],
});

const DECISION = buildBlock({
  key: 'decision',
  en: 'Decision unit and procurement',
  de: 'Entscheidungsgremium und Beschaffung',
  fr: 'Unité de décision et achats',
  cells: [
    {
      key: 'director', en: 'Director', de: 'Direktion', fr: 'Direction',
      type: 'person_ref',
      sources_hint: 'The organisation’s own team page or the annual report; the value is the id or PER- ref of a person on the record who is AFFILIATED with this organisation (get_organisation lists them) — never a number nobody looked up, never a name. A director not on the record is proposed with propose_person first.',
    },
    {
      key: 'board_president', en: 'Board president', de: 'Stiftungsrats-/Vorstandspräsidium', fr: 'Présidence du conseil',
      applies_when: INSTITUTIONS_ONLY,
      type: 'person_ref',
      sources_hint: 'The governance page, the annual report’s board list, or the commercial register (the "président" line of the entry, as moneyhouse.ch / business-monitor.ch mirror it when Zefix itself refuses robots). Same rule as director: an affiliated person on the record, or propose_person first.',
    },
    {
      key: 'head_finance', en: 'Head of finance', de: 'Leitung Finanzen', fr: 'Responsable des finances',
      type: 'person_ref',
      sources_hint: 'The team page, an annual report, or a signature block.',
    },
    {
      key: 'head_it', en: 'Head of IT', de: 'Leitung Informatik', fr: 'Responsable informatique',
      type: 'person_ref',
      sources_hint: 'The team page or a job advert; often absent, which is "not collected", not "none".',
    },
    {
      key: 'board_size', en: 'Board size', de: 'Grösse des Gremiums', fr: 'Taille du conseil',
      applies_when: INSTITUTIONS_ONLY,
      type: 'int',
      sources_hint: 'Count the members the annual report or the governance page actually lists.',
    },
    {
      key: 'procurement_regime', en: 'Procurement regime', de: 'Beschaffungsregime', fr: 'Régime des marchés',
      type: 'enum', fit_relevant: true,
      sources_hint: 'private_subsidised = the specific task is majority publicly financed (the AIMP subsidised-body principle), which pulls it under public procurement law even for a private body.',
      values: [
        ['public_law', 'Public law', 'Öffentliches Recht', 'Droit public'],
        ['private_subsidised', 'Private, subsidised task', 'Privat, subventionierte Aufgabe', 'Privé, tâche subventionnée'],
        ['private', 'Private', 'Privat', 'Privé'],
      ],
    },
    {
      key: 'direct_award_threshold_chf', en: 'Direct award threshold', de: 'Schwellenwert freihändige Vergabe', fr: 'Seuil de gré à gré',
      type: 'number', unit: 'CHF',
      sources_hint: 'NO DEFAULT. Read from the CURRENT AIMP 2019 / LMP-VD annex for the regime that applies (services and supplies, gré à gré), and only once procurement_regime is known; a threshold inferred from the regime rather than read for THIS organisation is `estimated` (confidence at most 0.7). Never a figure remembered from a draft.',
    },
    {
      key: 'consultancies_named', en: 'Consultancies named', de: 'Genannte Beratungsfirmen', fr: 'Sociétés de conseil citées',
      type: 'text',
      sources_hint: 'Named in the annual report, a tender award or a press item. Free text, never compared.',
    },
    {
      key: 'auditor', en: 'Auditor', de: 'Revisionsstelle', fr: 'Organe de révision',
      type: 'text',
      sources_hint: 'The annual report’s audit page or the commercial register entry ("organe de révision" — moneyhouse.ch / business-monitor.ch mirror it when Zefix refuses robots). The firm’s name and seat, e.g. "Fiduconsult Fidyver SA, Montagny-près-Yverdon". Free text, never compared.',
    },
  ],
});

// The operator's newest raster edit (2026-09-05, BUILD-BRIEF-R5 §1.7): a commissioning channel's
// own volumes, asked only of the bodies that administer one — never of the organisations they
// pay. `channel.measure_participants` is the payer aggregate's denominator (§6.1's Network route).
// Gated on `subsector` and not `legal_form`: nothing in this codebase writes `legal_form` (neither
// `institutions.v1.ts` nor `scripts/import-institutions.ts` sets it, and no `profile.identity.legal_form`
// fact is filed for a payer body), so a `legal_form` gate is never held and, per `appliesTo`'s "no
// context, or a context with nothing held → asked" rule, applies to everyone instead of the payer
// bodies §1.7 means. `import-institutions.ts` DOES file `profile.identity.subsector = 'public_admin'`
// for every institutions.v1.ts entry (`public_body`, `umbrella`, `commune`, `federal` alike), so
// gating on that held value follows the rows the importer actually marks.
const CHANNEL_PAYER_FORMS: AppliesWhen = { subsector: ['public_admin'] };

const CHANNEL = buildBlock({
  key: 'channel',
  en: 'Channel volumes',
  de: 'Kanalvolumen',
  fr: 'Volumes du canal',
  cells: [
    {
      key: 'new_intake', en: 'New intake (year)', de: 'Neuzugänge (Jahr)', fr: 'Nouveaux entrants (année)',
      applies_when: CHANNEL_PAYER_FORMS,
      type: 'number', unit: 'persons/year',
      sources_hint: 'The channel\'s own annual report or statistical yearbook — new entrants to the channel in the year (`as_of` the year\'s 31.12; `approx` allowed for a proxy count, e.g. SEM entries used for EVAM). A proxy figure is filed `estimated`, with the note saying why.',
    },
    {
      key: 'active_beneficiaries', en: 'Active beneficiaries', de: 'Aktive Klientinnen und Klienten', fr: 'Bénéficiaires actifs',
      applies_when: CHANNEL_PAYER_FORMS,
      type: 'number', unit: 'persons',
      sources_hint: 'The channel\'s own annual report or statistical yearbook — the caseload at the year\'s end (a STATUS, not a flow). A proxy figure is filed `estimated`, with the note saying why.',
    },
    {
      key: 'measure_participants', en: 'Measure participants (year)', de: 'Massnahmenteilnehmende (Jahr)', fr: 'Participants à une mesure (année)',
      applies_when: CHANNEL_PAYER_FORMS,
      type: 'number', unit: 'persons/year',
      sources_hint: 'The channel\'s own annual report or statistical yearbook — people who received at least one insertion or training measure in the year (people, not measures counted twice). This is the denominator the Network page\'s payer aggregate reads against the organisations naming this channel. A proxy figure is filed `estimated`, with the note saying why.',
    },
    {
      key: 'definition_note', en: 'Definition note', de: 'Definitionshinweis', fr: 'Note de définition',
      applies_when: CHANNEL_PAYER_FORMS,
      type: 'text',
      sources_hint: 'What the source actually counts — dossiers vs persons, measures vs people, a status vs a flow. Free text, never scored.',
    },
  ],
});

const TRIGGERS = buildBlock({
  key: 'triggers',
  en: 'Triggers',
  de: 'Auslöser',
  fr: 'Déclencheurs',
  computed: true,
  cells: [
    { key: 'leadership_change_12m', en: 'Leadership change (12m)', de: 'Führungswechsel (12 Mt.)', fr: 'Changement de direction (12 mois)', type: 'bool', sources_hint: 'Computed from `move` and `board` signals and from affiliations that ended.' },
    { key: 'new_strategy_period', en: 'New strategy period', de: 'Neue Strategieperiode', fr: 'Nouvelle période stratégique', type: 'bool', sources_hint: 'Computed from a change to operating.strategy_period_end.' },
    { key: 'merger_12m', en: 'Merger (12m)', de: 'Fusion (12 Mt.)', fr: 'Fusion (12 mois)', type: 'bool', sources_hint: 'Computed from SHAB publications and `mention` signals.' },
    { key: 'regulatory_deadline_12m', en: 'Regulatory deadline (12m)', de: 'Regulatorische Frist (12 Mt.)', fr: 'Échéance réglementaire (12 mois)', type: 'bool', sources_hint: 'Computed from `budget` and `mention` signals naming a dated obligation.' },
    { key: 'tender_12m', en: 'Tender (12m)', de: 'Ausschreibung (12 Mt.)', fr: 'Appel d’offres (12 mois)', type: 'bool', sources_hint: 'Computed from `tender` signals.' },
    { key: 'digital_hiring_12m', en: 'Digital hiring (12m)', de: 'Digitale Stellen (12 Mt.)', fr: 'Recrutement numérique (12 mois)', type: 'bool', sources_hint: 'Computed from `careers` monitor signals matching a digital vocabulary.' },
    { key: 'funding_shift_12m', en: 'Funding shift (12m)', de: 'Finanzierungsverschiebung (12 Mt.)', fr: 'Changement de financement (12 mois)', type: 'bool', sources_hint: 'Computed from `budget` signals and from a change to size_money.funding_model.' },
    { key: 'crisis_news_12m', en: 'Crisis in the news (12m)', de: 'Krise in den Medien (12 Mt.)', fr: 'Crise médiatique (12 mois)', type: 'bool', sources_hint: 'Computed from `risk` signals.' },
  ],
});

/** The raster. Seven blocks; the last is computed and is never proposed. */
export const RASTER: RasterBlock[] = [IDENTITY, MANDATE, SIZE_MONEY, OPERATING, DECISION, CHANNEL, TRIGGERS];

/** The block whose cells the nightly `profile.compute` writes and no agent may propose. */
export const COMPUTED_BLOCKS: readonly string[] = RASTER.filter((b) => b.computed).map((b) => b.key);

/** The blocks a `profile` research step may fill. */
export const PROPOSABLE_BLOCKS: RasterBlock[] = RASTER.filter((b) => !b.computed);

// ── addressing ──────────────────────────────────────────────────────────────

/** `profile.identity.legal_form`. The one place this string is composed. */
export function cellKey(block: string, cell: string): string {
  return `profile.${block}.${cell}`;
}

export interface FoundCell {
  block: RasterBlock;
  cell: RasterCell;
  key: string;
}

const BY_KEY = new Map<string, FoundCell>();
for (const block of RASTER) {
  for (const cell of block.cells) {
    BY_KEY.set(cellKey(block.key, cell.key), { block, cell, key: cellKey(block.key, cell.key) });
  }
}

/** `findCell('profile.identity.legal_form')`. Also accepts the bare `identity.legal_form`. */
export function findCell(key: string): FoundCell | null {
  const k = String(key ?? '').trim();
  return BY_KEY.get(k) ?? BY_KEY.get(`profile.${k}`) ?? null;
}

/** Every addressable cell key, in raster order. */
export const CELL_KEYS: string[] = [...BY_KEY.keys()];

/** The cells coverage counts: comparable, and not in a computed block. */
export const COVERED_KEYS: string[] = RASTER
  .filter((b) => !b.computed)
  .flatMap((b) => b.cells.filter((c) => c.comparable).map((c) => cellKey(b.key, c.key)));

// ── labels ──────────────────────────────────────────────────────────────────

export type RasterLang = 'en' | 'de' | 'fr';

export interface RasterLabelSet {
  /** block key → label */
  blocks: Record<string, string>;
  /** `block.cell` → label */
  cells: Record<string, string>;
  /** `block.cell.value` → label */
  values: Record<string, string>;
}

const EN_LABELS: RasterLabelSet = { blocks: {}, cells: {}, values: {} };
for (const block of RASTER) {
  EN_LABELS.blocks[block.key] = block.label;
  for (const cell of block.cells) {
    EN_LABELS.cells[`${block.key}.${cell.key}`] = cell.label;
    for (const v of [...(cell.values ?? []), ...(cell.bands ?? [])]) {
      EN_LABELS.values[`${block.key}.${cell.key}.${v.value}`] = v.label;
    }
  }
}

/** Every block, cell and value label in the three console languages. */
export const RASTER_LABELS: Record<RasterLang, RasterLabelSet> = {
  en: EN_LABELS,
  de: { blocks: DE_BLOCKS, cells: DE_CELLS, values: DE_VALUES },
  fr: { blocks: FR_BLOCKS, cells: FR_CELLS, values: FR_VALUES },
};

/**
 * `labelFor('fr', 'identity')` · `labelFor('fr', 'identity', 'legal_form')` ·
 * `labelFor('fr', 'identity', 'legal_form', 'fondation')`.
 *
 * Falls back to English, then to the raw token — a missing translation shows the English word,
 * never an empty cell.
 */
export function labelFor(lang: RasterLang, block: string, cell?: string, value?: string): string {
  const path = value != null && cell != null ? `${block}.${cell}.${value}`
    : cell != null ? `${block}.${cell}`
      : block;
  const bucket = value != null ? 'values' : cell != null ? 'cells' : 'blocks';
  return RASTER_LABELS[lang][bucket][path] ?? EN_LABELS[bucket][path] ?? (value ?? cell ?? block);
}

// ── parsing ─────────────────────────────────────────────────────────────────

export type RasterCellValue = string | number | boolean | string[];

// HOW EXACT A NUMBER IS (2026-09-05, the SemoNord benchmark). A page that says "environ 250
// jeunes par année" or four measure pages whose places add up to at least 116 give a number a
// comparable cell can hold — 250, 116 — and a nuance a bare integer loses. The nuance is a
// QUALIFIER beside the value, never inside it: `facts.value` stays the number every reader
// parses, `facts.qualifier` says `at_least` (≥, "116+"), `at_most` (≤) or `approx` (~, environ).
// Absent means exact. A qualifier is not a method: an approximate figure read off a page is
// still `read`. `NumericQualifier`, `NUMERIC_QUALIFIERS`, `QUALIFIER_SIGN` and `splitQualifier`
// are DECLARED in `network.v1.ts` (which has zero imports, so this file can import them beside
// `REGIME_VALUES` without a cycle) and re-exported at the top of this file.

/** The one text a reference cell holds when the relation is COUNTED AND ABSENT — an
 *  organisation with no parent body. Not "not collected": a verdict read off the source. */
export const REF_NONE = 'none';
const REF_NONE_WORDS = new Set(['none', 'aucun', 'aucune', 'kein', 'keine', 'independent', 'independant', 'indépendant', 'indépendante', 'unabhängig', 'unabhangig', 'no parent', 'sans']);

export type RasterRejectionCode =
  | 'empty'
  | 'not_in_enum'
  | 'not_a_list'
  | 'not_a_number'
  | 'not_an_integer'
  | 'negative'
  | 'out_of_range'
  | 'not_a_boolean'
  | 'not_a_reference'
  | 'too_long';

export interface RasterParseOk {
  ok: true;
  /** The typed value. `enum_multi` is a de-duplicated array in the raster's own order. */
  value: RasterCellValue;
  /** The canonical string to store in `facts.value`. */
  text: string;
  /** For an int or a number: how exact it is (`facts.qualifier`). Absent or null = exact. */
  qualifier?: NumericQualifier | null;
}

export interface RasterParseError {
  ok: false;
  code: RasterRejectionCode;
  /** An actionable sentence: what was wrong AND what would be accepted. */
  reason: string;
}

export type RasterParseResult = RasterParseOk | RasterParseError;

/** Free text is capped so a paragraph cannot be smuggled into a "cell". */
export const TEXT_MAX = 200;

const YEAR_MIN = 1200;
const YEAR_MAX = 2100;

const reject = (code: RasterRejectionCode, reason: string): RasterParseError => ({ ok: false, code, reason });

function listOf(cell: RasterCell): RasterValue[] {
  return cell.type === 'band' ? (cell.bands ?? []) : (cell.values ?? []);
}

function allowed(cell: RasterCell): string[] {
  return listOf(cell).map((v) => v.value);
}

function numberOf(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  const s = String(raw ?? '').trim().replace(/[’' \s]/g, '').replace(/,/g, '.');
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Turn whatever a model, an importer or an operator handed us into the cell's own type, or say
 * in one actionable sentence why it is not a value for this cell.
 *
 * Nothing else in the codebase may interpret a raster value. A cell that does not parse is
 * DROPPED and reported — never coerced into something that looks like a value.
 */
export function parseCell(cell: RasterCell, raw: unknown): RasterParseResult {
  if (raw === null || raw === undefined) return reject('empty', `${cell.label} was empty; a cell with no value stays "not collected".`);

  switch (cell.type) {
    case 'enum':
    case 'band': {
      const s = String(raw).trim();
      if (s === '') return reject('empty', `${cell.label} was empty; a cell with no value stays "not collected".`);
      const list = allowed(cell);
      if (!list.includes(s)) {
        return reject('not_in_enum', `'${s}' is not a value of ${cell.label}. Accepted: ${list.join(', ')}.`);
      }
      return { ok: true, value: s, text: s };
    }

    case 'enum_multi': {
      const list = allowed(cell);
      let items: string[];
      if (Array.isArray(raw)) items = raw.map((x) => String(x).trim());
      else if (typeof raw === 'string') items = raw.split(',').map((x) => x.trim());
      else return reject('not_a_list', `${cell.label} takes a list of values from: ${list.join(', ')}.`);
      items = items.filter((x) => x !== '');
      if (items.length === 0) return reject('empty', `${cell.label} was an empty list; a cell with no value stays "not collected".`);
      const bad = items.find((x) => !list.includes(x));
      if (bad !== undefined) {
        return reject('not_in_enum', `'${bad}' is not a value of ${cell.label}. Accepted: ${list.join(', ')}.`);
      }
      // De-duplicated and put back into the raster's own order, so two agents that saw the same
      // three values in a different order produce the SAME text and do not re-propose each other.
      const kept = list.filter((v) => items.includes(v));
      return { ok: true, value: kept, text: kept.join(',') };
    }

    case 'int': {
      // "≥116", "116+", "environ 250": the qualifier rides beside the value, the value stays a number.
      const split = typeof raw === 'string' ? splitQualifier(raw) : { qualifier: null, rest: raw };
      const n = numberOf(split.rest);
      if (n === null) return reject('not_a_number', `${cell.label} takes a whole number${cell.unit ? ` of ${cell.unit}` : ''} — optionally qualified: "≥116", "116+", "~250"; '${String(raw)}' is not one.`);
      if (!Number.isInteger(n)) return reject('not_an_integer', `${cell.label} takes a whole number; ${n} has a fraction.`);
      if (n < 0) return reject('negative', `${cell.label} cannot be negative.`);
      return { ok: true, value: n, text: String(n), qualifier: split.qualifier };
    }

    case 'number': {
      const split = typeof raw === 'string' ? splitQualifier(raw) : { qualifier: null, rest: raw };
      const n = numberOf(split.rest);
      if (n === null) return reject('not_a_number', `${cell.label} takes a number${cell.unit ? ` in ${cell.unit}` : ''} — optionally qualified: "≥150000", "~3.2"; '${String(raw)}' is not one.`);
      if (cell.unit === '%' && (n < 0 || n > 100)) {
        return reject('out_of_range', `${cell.label} is a percentage: it must be between 0 and 100, not ${n}.`);
      }
      return { ok: true, value: n, text: String(n), qualifier: split.qualifier };
    }

    case 'bool': {
      if (typeof raw === 'boolean') return { ok: true, value: raw, text: raw ? 'true' : 'false' };
      const s = String(raw).trim().toLowerCase();
      if (['true', 'yes', 'oui', 'ja', '1'].includes(s)) return { ok: true, value: true, text: 'true' };
      if (['false', 'no', 'non', 'nein', '0'].includes(s)) return { ok: true, value: false, text: 'false' };
      return reject('not_a_boolean', `${cell.label} is true or false; '${String(raw)}' is neither. An unknown answer is not 'false' — leave the cell out.`);
    }

    case 'year': {
      const n = numberOf(raw);
      if (n === null || !Number.isInteger(n)) return reject('not_an_integer', `${cell.label} takes a four-digit year; '${String(raw)}' is not one.`);
      if (n < YEAR_MIN || n > YEAR_MAX) return reject('out_of_range', `${cell.label} must be a year between ${YEAR_MIN} and ${YEAR_MAX}, not ${n}.`);
      return { ok: true, value: n, text: String(n) };
    }

    case 'month': {
      const n = numberOf(raw);
      if (n === null || !Number.isInteger(n)) return reject('not_an_integer', `${cell.label} takes a month as a number from 1 to 12; '${String(raw)}' is not one.`);
      if (n < 1 || n > 12) return reject('out_of_range', `${cell.label} must be a month from 1 to 12, not ${n}.`);
      return { ok: true, value: n, text: String(n) };
    }

    case 'person_ref':
    case 'organisation_ref': {
      const prefix = cell.type === 'person_ref' ? 'PER' : 'ORG';
      if (typeof raw === 'number' || /^\d+$/.test(String(raw).trim())) {
        const n = Number(String(raw).trim());
        if (!Number.isInteger(n) || n <= 0) return reject('not_a_reference', `${cell.label} takes the id of a ${cell.type === 'person_ref' ? 'person' : 'organisation'} on the record, or a ${prefix}- reference.`);
        return { ok: true, value: n, text: String(n) };
      }
      // A counted absence — "independent association, no parent body" — is `none`, and it is a
      // value: the source said there is nobody, which is not the same as nobody having looked.
      if (REF_NONE_WORDS.has(String(raw).trim().toLowerCase())) return { ok: true, value: REF_NONE, text: REF_NONE };
      const s = String(raw).trim().toUpperCase();
      if (!new RegExp(`^${prefix}-\\d{3,}$`).test(s)) {
        return reject('not_a_reference', `'${String(raw)}' is not a ${prefix}- reference. ${cell.label} takes an id on the record, a reference such as ${prefix}-0012, or 'none' when the source says there is none — never a name.`);
      }
      return { ok: true, value: s, text: s };
    }

    case 'text': {
      const s = String(raw).trim().replace(/\s+/g, ' ');
      if (s === '') return reject('empty', `${cell.label} was empty; a cell with no value stays "not collected".`);
      if (s.length > TEXT_MAX) return reject('too_long', `${cell.label} holds a short identifier, not prose: at most ${TEXT_MAX} characters (this one had ${s.length}). Put the story in the dossier.`);
      return { ok: true, value: s, text: s };
    }
  }
}

/** Parse by key: `parseByKey('profile.size_money.fte', '845.13')`. Null when the key is unknown. */
export function parseByKey(key: string, raw: unknown): RasterParseResult | null {
  const found = findCell(key);
  if (!found) return null;
  return parseCell(found.cell, raw);
}

/**
 * Which band a measured number falls in — `bandFor(fteBandCell, 845.13)` → '500_999'.
 * `min` inclusive, `max` exclusive; null when the cell is not a band or the number is outside.
 */
export function bandFor(cell: RasterCell, n: number): string | null {
  if (cell.type !== 'band' || !cell.bands || !Number.isFinite(n)) return null;
  for (const b of cell.bands) {
    const aboveMin = b.min === null || n >= b.min;
    const belowMax = b.max === null || n < b.max;
    if (aboveMin && belowMax) return b.value;
  }
  return null;
}

// ── coverage ────────────────────────────────────────────────────────────────

export interface RasterCoverage {
  /** How many cells COULD be filled: comparable, outside the computed triggers block. */
  defined: number;
  /** How many of those actually hold a value. */
  filled: number;
  /** filled / defined as a whole percent. 0 when nothing is defined — never NaN. */
  pct: number;
  /** Cells that do not apply to this organisation (R3g §7); absent when none or no context. */
  not_applicable?: number;
  /** Applicable cells a run searched and marked not_found (R3/2026-09-04); absent when none. */
  not_found?: number;
}

/**
 * What the caller may hand `coverage()`: the rows as they come out of `facts`, a bare list of
 * keys, or a key → value map. A key that is not a covered cell is ignored; an empty value is
 * not a fill.
 */
export type CoverageInput =
  | Iterable<string | { key: string; value?: string | null; method?: string | null }>
  | Record<string, unknown>;

const isFilled = (v: unknown): boolean => v !== null && v !== undefined && String(v).trim() !== '';

function filledKeys(facts: CoverageInput): Set<string> {
  const out = new Set<string>();
  const add = (key: unknown, value: unknown, hasValue: boolean): void => {
    const found = findCell(String(key ?? ''));
    if (!found) return;
    if (hasValue && !isFilled(value)) return;
    out.add(found.key);
  };
  const iterable = facts as { [Symbol.iterator]?: unknown } | null;
  if (iterable && typeof iterable[Symbol.iterator] === 'function') {
    for (const entry of facts as Iterable<string | { key: string; value?: string | null }>) {
      if (typeof entry === 'string') add(entry, null, false);
      else if (entry && typeof entry === 'object') add(entry.key, entry.value, 'value' in entry);
    }
  } else if (facts && typeof facts === 'object') {
    for (const [key, value] of Object.entries(facts as Record<string, unknown>)) add(key, value, true);
  }
  return out;
}

/**
 * Coverage over the whole raster: only comparable cells, and never the computed triggers block.
 * ARITHMETIC MAKES NO CLAIM — a coverage figure carries no claim mark in the UI.
 */
/** The organisation a coverage figure is computed for: what its held legal form and sub-sectors say. */
export interface ApplicabilityContext {
  legal_form?: string | null;
  subsector?: readonly string[] | null;
}

/** Whether a cell is asked of an organisation. No context, or a context with nothing held → asked. */
export function appliesTo(cell: Pick<RasterCell, 'applies_when'>, ctx?: ApplicabilityContext | null): boolean {
  const w = cell.applies_when;
  if (!w || !ctx) return true;
  if (w.legal_form && ctx.legal_form && !w.legal_form.includes(ctx.legal_form)) return false;
  if (w.subsector && ctx.subsector && ctx.subsector.length && !ctx.subsector.some((x) => w.subsector!.includes(x))) return false;
  return true;
}

/** The comparable, non-computed keys an organisation is measured on: COVERED_KEYS minus what does not apply. */
export function applicableKeys(ctx?: ApplicabilityContext | null): string[] {
  if (!ctx) return COVERED_KEYS;
  const out: string[] = [];
  for (const block of RASTER) {
    if (block.computed) continue;
    for (const c of block.cells) if (c.comparable && appliesTo(c, ctx)) out.push(cellKey(block.key, c.key));
  }
  return out;
}

/** The applicable cells a run searched and marked not_found — a verdict, counted apart from "filled". */
function notFoundKeys(facts: CoverageInput, applicable: Set<string>): number {
  const iterable = facts as { [Symbol.iterator]?: unknown } | null;
  if (!iterable || typeof iterable[Symbol.iterator] !== 'function') return 0;
  const seen = new Set<string>();
  for (const entry of facts as Iterable<string | { key: string; value?: string | null; method?: string | null }>) {
    if (typeof entry === 'string' || entry.method !== 'not_found') continue;
    const found = findCell(entry.key);
    if (found && applicable.has(found.key)) seen.add(found.key);
  }
  return seen.size;
}

export function coverage(facts: CoverageInput, ctx?: ApplicabilityContext | null): RasterCoverage {
  const present = filledKeys(facts);
  const keys = applicableKeys(ctx);
  const defined = keys.length;
  let filled = 0;
  for (const key of keys) if (present.has(key)) filled += 1;
  const not_applicable = COVERED_KEYS.length - defined;
  const not_found = notFoundKeys(facts, new Set(keys));
  return { defined, filled, pct: defined === 0 ? 0 : Math.round((filled / defined) * 100), ...(not_applicable ? { not_applicable } : {}), ...(not_found ? { not_found } : {}) };
}

/** The same arithmetic per block — what `ProfileZone` prints beside each block head. */
export function coverageByBlock(facts: CoverageInput, ctx?: ApplicabilityContext | null): Record<string, RasterCoverage> {
  const present = filledKeys(facts);
  const out: Record<string, RasterCoverage> = {};
  for (const block of RASTER) {
    if (block.computed) continue;
    const keys = block.cells.filter((c) => c.comparable && appliesTo(c, ctx)).map((c) => cellKey(block.key, c.key));
    const filled = keys.filter((k) => present.has(k)).length;
    out[block.key] = {
      defined: keys.length,
      filled,
      pct: keys.length === 0 ? 0 : Math.round((filled / keys.length) * 100),
    };
  }
  return out;
}

// ── the schema the `profile` research step emits against ────────────────────

/** An estimate is never certain: BUILD-BRIEF-R3 §3 caps its confidence here. */
export const ESTIMATE_MAX_CONFIDENCE = 0.7;

type JsonSchema = Record<string, unknown>;

function valueSchema(cell: RasterCell): JsonSchema {
  switch (cell.type) {
    case 'enum':
    case 'band':
      return { type: 'string', enum: allowed(cell) };
    case 'enum_multi':
      // No `uniqueItems`: the operator's vLLM (xgrammar) refuses the whole request with a 500
      // when the keyword appears anywhere in the schema, which took the entire profile step
      // down. Nothing is lost — `parseCell` already de-duplicates and re-orders the list into
      // the raster's own order before it becomes a value.
      return { type: 'array', minItems: 1, items: { type: 'string', enum: allowed(cell) } };
    case 'int':
      return { type: 'integer', minimum: 0 };
    case 'number':
      return cell.unit === '%' ? { type: 'number', minimum: 0, maximum: 100 } : { type: 'number' };
    case 'bool':
      return { type: 'boolean' };
    case 'year':
      return { type: 'integer', minimum: YEAR_MIN, maximum: YEAR_MAX };
    case 'month':
      return { type: 'integer', minimum: 1, maximum: 12 };
    case 'person_ref':
      return { oneOf: [{ type: 'integer', minimum: 1 }, { type: 'string', pattern: '^PER-\\d{3,}$' }, { const: REF_NONE }] };
    case 'organisation_ref':
      return { oneOf: [{ type: 'integer', minimum: 1 }, { type: 'string', pattern: '^ORG-\\d{3,}$' }, { const: REF_NONE }] };
    case 'text':
      return { type: 'string', minLength: 1, maxLength: TEXT_MAX };
  }
}

function cellSchema(cell: RasterCell): JsonSchema {
  return {
    title: cell.label,
    description: cell.sources_hint,
    type: 'object',
    additionalProperties: false,
    required: ['value', 'method', 'quote'],
    properties: {
      value: valueSchema(cell),
      ...(cell.type === 'int' || cell.type === 'number'
        ? { qualifier: { enum: [...NUMERIC_QUALIFIERS], description: 'How exact the figure is: at_least (≥, a floor), at_most (≤), approx (~, "environ"). Omit for an exact figure.' } }
        : {}),
      method: { enum: ['read', 'estimated'], description: 'read = it is written on the page; estimated = you inferred it.' },
      confidence: { type: 'number', exclusiveMinimum: 0, maximum: 1 },
      quote: { type: 'string', minLength: 1, description: 'Verbatim, character for character, from a snapshot this run read.' },
      snapshot_id: { type: 'integer', minimum: 1 },
    },
    allOf: [
      {
        // read → the page said it, so name the page and quote it.
        if: { properties: { method: { const: 'read' } }, required: ['method'] },
        then: { required: ['quote', 'snapshot_id'] },
      },
      {
        // estimated → you inferred it, so say how sure you are, and never more than 0.7.
        if: { properties: { method: { const: 'estimated' } }, required: ['method'] },
        then: {
          required: ['quote', 'confidence'],
          properties: { confidence: { type: 'number', exclusiveMinimum: 0, maximum: ESTIMATE_MAX_CONFIDENCE } },
        },
      },
    ],
    ...(cell.unit ? { $comment: `unit: ${cell.unit}` } : {}),
  };
}

/** A restriction of `rasterJsonSchema()` to ONE block, and optionally to some of its cells. */
export interface RasterSchemaScope {
  /** The block key (`size_money`). Unknown → the schema carries no block at all. */
  block?: string;
  /** Cell keys — `fte`, `size_money.fte` or `profile.size_money.fte` all name the same cell.
   *  Cells of another block are ignored; an empty list keeps every cell of the block. */
  cells?: readonly string[];
}

/**
 * The JSON schema the `profile` research step emits against (BUILD-BRIEF-R3 §3): one object per
 * block, one object per cell, each `{ value, method, confidence, quote, snapshot_id }`.
 *
 * The COMPUTED triggers block is absent on purpose — a step that cannot name a trigger cannot
 * propose one, and the nightly job stays its only author.
 *
 * Every property is optional: a profile step fills what the pages actually said and leaves the
 * rest out, because "not collected" is a state the record keeps rather than a gap it fills.
 *
 * WITH A SCOPE (BUILD-BRIEF-R3d §2.4) the schema carries one block and only the cells named —
 * a few kilobytes instead of the whole raster's ~46 kB, which a reasoning model would otherwise
 * echo through on every call (RUN-0008: 39.6k output tokens for three already-held cells).
 * `validateProfileOutput` accepts such a partial block unchanged: it never required every cell.
 */
export function rasterJsonSchema(scope?: RasterSchemaScope): JsonSchema {
  const properties: Record<string, JsonSchema> = {};
  const wanted = scope?.cells?.length
    ? new Set(scope.cells.map((c) => {
        const found = findCell(c);
        return found ? found.key : cellKey(scope.block ?? '', c);
      }))
    : null;
  for (const block of PROPOSABLE_BLOCKS) {
    if (scope?.block && block.key !== scope.block) continue;
    const cells: Record<string, JsonSchema> = {};
    for (const cell of block.cells) {
      if (wanted && !wanted.has(cellKey(block.key, cell.key))) continue;
      cells[cell.key] = cellSchema(cell);
    }
    if (scope && !Object.keys(cells).length) continue;
    properties[block.key] = {
      title: block.label,
      type: 'object',
      additionalProperties: false,
      properties: cells,
    };
  }
  return {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    $id: scope?.block ? `crm:raster.${RASTER_VERSION}.${scope.block}` : `crm:raster.${RASTER_VERSION}`,
    title: scope?.block ? `Organisation raster ${RASTER_VERSION} · ${scope.block}` : `Organisation raster ${RASTER_VERSION}`,
    description:
      'One cited value per cell. Omit a cell you did not read rather than guessing it: an absent cell reads "not collected", which is the truth, and a wrong cell is a claim an operator has to unpick.',
    type: 'object',
    additionalProperties: false,
    properties,
  };
}

/** How many cells a `profile` step may propose — the "of n cells" of the Profile zone. */
export const PROPOSABLE_CELL_COUNT: number = PROPOSABLE_BLOCKS.reduce((n, b) => n + b.cells.length, 0);

// ── the structural check behind the schema ──────────────────────────────────

export interface ProfileCellOutput {
  value: unknown;
  qualifier?: NumericQualifier | null;
  method?: 'read' | 'estimated';
  confidence?: number | null;
  quote?: string | null;
  snapshot_id?: number | null;
}

export interface ProfileOutputError {
  key: string;
  code: RasterRejectionCode | 'unknown_cell' | 'computed_block' | 'missing_method' | 'missing_quote' | 'missing_snapshot' | 'missing_confidence' | 'confidence_too_high';
  reason: string;
}

export interface ProfileOutputCell {
  key: string;
  block: string;
  cell: string;
  value: RasterCellValue;
  /** The canonical text to store in `facts.value`. */
  text: string;
  /** How exact a figure is (int and number cells); null = exact. */
  qualifier: NumericQualifier | null;
  method: 'read' | 'estimated';
  confidence: number | null;
  quote: string;
  snapshot_id: number | null;
}

export interface ProfileOutputCheck {
  ok: boolean;
  cells: ProfileOutputCell[];
  errors: ProfileOutputError[];
}

/**
 * The structural half of `rasterJsonSchema()`, in code — the check `services/profile.ts` runs
 * before it files a single `propose_fact`.
 *
 * It exists BESIDE the schema rather than instead of it: the schema is what the model is shown,
 * this is what the server trusts. A model that ignores the schema, an operator pasting an
 * export, and a future transport all land here, and the rules are the same for all three:
 *   · the cell must exist, and must not be in the computed block;
 *   · the value must parse against its type (`parseCell`);
 *   · `read` requires a verbatim quote AND the snapshot it came from;
 *   · `estimated` requires a supporting quote AND a confidence in (0, 0.7].
 *
 * Nothing is coerced. A cell that fails is reported with an actionable sentence and dropped.
 */
/**
 * A multi-value cell answered as a LIST of cells — one `{ value, method, confidence, quote,
 * snapshot_id }` per value, each with its own quote. qwen3 answers `service_lines` and
 * `beneficiary_groups` this way in every mandate call on Eben-Hézer (RUN-0004/0005, fix round 2
 * live): the raster read `value` off the array, found none, dropped both as "empty", and the
 * block was re-asked for them — a retry that climbed the token ladder and timed out. One quote
 * per value is better evidence than one quote for a list, not worse; the list is folded into the
 * one cell the raster expects: the values in the order given, `read` only when every item was
 * read, the lowest confidence, and the first cited item's quote and snapshot — the passage the
 * proposal cites. Anything else (a single cell, a bare list of tokens, nothing) is returned as it
 * came; the cell's type is not consulted here, `parseCell` says what a list means for it.
 */
export function foldCellList(raw: unknown): unknown {
  if (!Array.isArray(raw) || raw.length === 0) return raw;
  const items = raw.filter((x): x is ProfileCellOutput => !!x && typeof x === 'object' && !Array.isArray(x) && 'value' in (x as object));
  if (items.length !== raw.length) return raw;
  const values = items.flatMap((i) => (Array.isArray(i.value) ? i.value : [i.value])).filter((v) => v !== null && v !== undefined && String(v).trim() !== '');
  const cited = items.find((i) => typeof i.quote === 'string' && i.quote.trim() !== '');
  const confidences = items.map((i) => (i.confidence === null || i.confidence === undefined ? NaN : Number(i.confidence))).filter((n) => Number.isFinite(n));
  return {
    value: values,
    method: items.every((i) => i.method === 'read') ? 'read' : items.some((i) => i.method === 'read' || i.method === 'estimated') ? 'estimated' : undefined,
    confidence: confidences.length ? Math.min(...confidences) : null,
    quote: cited?.quote ?? null,
    snapshot_id: cited?.snapshot_id ?? null,
  } satisfies ProfileCellOutput;
}

export function validateProfileOutput(output: unknown): ProfileOutputCheck {
  const cells: ProfileOutputCell[] = [];
  const errors: ProfileOutputError[] = [];
  if (!output || typeof output !== 'object') {
    return { ok: false, cells, errors: [{ key: '', code: 'unknown_cell', reason: 'The profile step must return an object of blocks.' }] };
  }
  for (const [blockKey, blockValue] of Object.entries(output as Record<string, unknown>)) {
    if (!blockValue || typeof blockValue !== 'object') {
      errors.push({ key: blockKey, code: 'unknown_cell', reason: `'${blockKey}' is not a block of the raster.` });
      continue;
    }
    for (const [cellName, rawCell] of Object.entries(blockValue as Record<string, unknown>)) {
      const key = cellKey(blockKey, cellName);
      const found = findCell(key);
      if (!found) {
        errors.push({ key, code: 'unknown_cell', reason: `'${key}' is not a cell of raster ${RASTER_VERSION}.` });
        continue;
      }
      if (found.block.computed) {
        errors.push({ key, code: 'computed_block', reason: `'${key}' is computed nightly from signals and events; it cannot be proposed.` });
        continue;
      }
      const c = (foldCellList(rawCell) ?? {}) as ProfileCellOutput;
      const parsed = parseCell(found.cell, (c as { value?: unknown }).value);
      if (!parsed.ok) {
        errors.push({ key, code: parsed.code, reason: parsed.reason });
        continue;
      }
      const method = c.method;
      if (method !== 'read' && method !== 'estimated') {
        errors.push({ key, code: 'missing_method', reason: `'${key}' needs method 'read' (the page says it) or 'estimated' (you inferred it).` });
        continue;
      }
      const quote = typeof c.quote === 'string' ? c.quote.trim() : '';
      if (quote === '') {
        errors.push({ key, code: 'missing_quote', reason: `'${key}' has no quote. Every value cites the words it was read from, verbatim.` });
        continue;
      }
      // A NEGATIVE id is a source the pre-fill holds (a register line — BUILD-BRIEF-R3d §2.1),
      // never a snapshot and never something the model wrote: the citation check resolves it
      // against the run's own sources. Zero names nothing.
      const snapshotId = c.snapshot_id === null || c.snapshot_id === undefined ? null : Number(c.snapshot_id);
      if (method === 'read' && (snapshotId === null || !Number.isInteger(snapshotId) || snapshotId === 0)) {
        errors.push({ key, code: 'missing_snapshot', reason: `'${key}' was read, so it must name the snapshot it was read from.` });
        continue;
      }
      const confidence = c.confidence === null || c.confidence === undefined ? null : Number(c.confidence);
      if (method === 'estimated') {
        if (confidence === null || !Number.isFinite(confidence) || confidence <= 0) {
          errors.push({ key, code: 'missing_confidence', reason: `'${key}' is an estimate, so it must carry a confidence above 0 and at most ${ESTIMATE_MAX_CONFIDENCE}.` });
          continue;
        }
        if (confidence > ESTIMATE_MAX_CONFIDENCE) {
          errors.push({ key, code: 'confidence_too_high', reason: `'${key}' is an estimate: its confidence cannot exceed ${ESTIMATE_MAX_CONFIDENCE} (given ${confidence}).` });
          continue;
        }
      }
      const declaredQualifier = NUMERIC_QUALIFIERS.includes(c.qualifier as NumericQualifier) ? (c.qualifier as NumericQualifier) : null;
      cells.push({
        key,
        block: blockKey,
        cell: cellName,
        value: parsed.value,
        text: parsed.text,
        qualifier: (found.cell.type === 'int' || found.cell.type === 'number') ? (parsed.qualifier ?? declaredQualifier) : null,
        method,
        confidence: confidence !== null && Number.isFinite(confidence) ? confidence : null,
        quote,
        snapshot_id: snapshotId,
      });
    }
  }
  return { ok: errors.length === 0, cells, errors };
}
